import { useState } from 'react';
import { vendorAssetUrl } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Modal } from '@mypoz/ui';

import { legacyAssetOrigin } from '@/lib/legacy-api.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';

export interface QrDialogProps {
  /** The account's display name, e.g. "DuitNow". */
  name: string;
  /** Who the payment goes to, for the picture's description. */
  accountName?: string;
  /** The vendor's `accimg` path. */
  qrPath: string;
  onClose: () => void;
}

/**
 * An account's QR picture (MYP-61). Live it is a DuitNow QR poster, 2480×3508, so it is scaled to
 * fit the screen, with a link to open it full size — on a phone, to zoom or save it. Loaded only
 * when the dialog opens.
 */
export function QrDialog({ name, accountName, qrPath, onClose }: QrDialogProps) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const url = vendorAssetUrl(qrPath, legacyAssetOrigin);

  return (
    <Modal
      title={t('chinaPayment.payTo.qrTitle', { name })}
      onClose={onClose}
      className="w-[min(94vw,28rem)]"
    >
      {failed ? (
        <p className="py-6 text-center text-sm text-ink">{t('chinaPayment.payTo.qrFailed')}</p>
      ) : (
        <img
          src={url}
          alt={t('chinaPayment.payTo.qrAlt', { name, accountName: accountName ?? name })}
          onError={() => setFailed(true)}
          className="mx-auto max-h-[65vh] w-auto rounded-md object-contain"
        />
      )}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={actionLinkClass('inline-flex min-h-11 items-center text-sm lg:min-h-0')}
        >
          {t('chinaPayment.payTo.openFullSize')}
        </a>
        <button type="button" onClick={onClose} className={buttonClass('secondary')}>
          {t('actions.close')}
        </button>
      </div>
    </Modal>
  );
}
